import { useCallback, useMemo } from 'react';

import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import BusinessIcon from '@mui/icons-material/Business';
import StoreIcon from '@mui/icons-material/Store';
import { DataGrid } from '@mui/x-data-grid';
import type {
  GridColDef,
  GridRenderCellParams,
  GridRowParams,
  GridRowSelectionModel,
} from '@mui/x-data-grid';

import { EMPTY_STRING } from '@e-socna/empty';
import { formatSiret } from '@e-socna/third-parties';

import type { EstablishmentListItem } from './utils/build-establishments-list.util';

interface Row {
  readonly id: number;
  readonly isAlreadyCreated: boolean;
  readonly isHeadOffice: boolean;
  readonly adresse: string;
  readonly siret: string;
}

const columns: readonly GridColDef<Row>[] = [
  {
    field: 'isHeadOffice',
    flex: 1,
    headerName: 'Type',
    renderCell: ({
      row,
      value,
    }: Readonly<GridRenderCellParams<Row, boolean>>) => (
      <Stack
        direction="row"
        sx={{ alignItems: 'center', gap: 0.75, height: '100%' }}
      >
        {value ? (
          <BusinessIcon color="action" fontSize="small" />
        ) : (
          <StoreIcon color="action" fontSize="small" />
        )}
        <Typography variant="body2">
          {value ? 'Siège' : 'Établissement'}
        </Typography>
        {row.isAlreadyCreated ? (
          <Chip color="success" label="Déjà créé" size="small" />
        ) : null}
      </Stack>
    ),
  },
  { field: 'siret', flex: 1, headerName: 'SIRET' },
  { field: 'adresse', flex: 2, headerName: 'Adresse' },
];

interface Props {
  readonly alreadyCreatedIndices: ReadonlySet<number>;
  readonly establishments: readonly EstablishmentListItem[];
  readonly onRowSelectionModelChange: (model: GridRowSelectionModel) => void;
  readonly rowSelectionModel: GridRowSelectionModel;
}

export const EstablishmentsSelectionGrid = ({
  alreadyCreatedIndices,
  establishments,
  onRowSelectionModelChange,
  rowSelectionModel,
}: Readonly<Props>) => {
  const isRowSelectable = useCallback(
    ({ id }: Readonly<GridRowParams<Row>>) =>
      !alreadyCreatedIndices.has(Number(id)),
    [alreadyCreatedIndices],
  );

  const rows: readonly Row[] = useMemo(
    () =>
      establishments.map(({ establishment, isHeadOffice }, index) => ({
        id: index,
        isAlreadyCreated: alreadyCreatedIndices.has(index),
        isHeadOffice,
        siret: formatSiret(establishment.siret),
        adresse: establishment.adresse ?? EMPTY_STRING,
      })),
    [alreadyCreatedIndices, establishments],
  );

  return (
    <Box sx={{ width: '100%', minWidth: { sm: 500 } }}>
      <DataGrid
        autoHeight
        checkboxSelection
        columns={[...columns]}
        disableColumnMenu
        disableRowSelectionOnClick
        hideFooter
        isRowSelectable={isRowSelectable}
        onRowSelectionModelChange={onRowSelectionModelChange}
        rowSelectionModel={rowSelectionModel}
        rows={[...rows]}
      />
    </Box>
  );
};
